import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowClockwise, Receipt, Phone, Lock, SignOut, EnvelopeSimple } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PASS_KEY = "ds_admin_pass";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};
const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

const statusStyle = (s) => {
  if (s === "Paid online") return "bg-brandblue text-white";
  if (s === "Pay on pickup") return "bg-sun text-ink";
  if (s === "Enquiry") return "bg-ink/10 text-ink";
  return "bg-white text-ink border border-ink/20";
};

export default function AdminBookings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [payFilter, setPayFilter] = useState("all"); // all | paid | unpaid
  const [recapBusy, setRecapBusy] = useState(false);

  const load = useCallback(async (code) => {
    const passcode = code ?? localStorage.getItem(PASS_KEY) ?? "";
    if (!passcode) { setAuthed(false); return; }
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, { headers: { "X-Admin-Passcode": passcode } });
      localStorage.setItem(PASS_KEY, passcode);
      setRows(data.bookings || []);
      setAuthed(true);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(PASS_KEY);
        setAuthed(false);
        setError(code ? "Incorrect passcode. Try again." : "");
      } else {
        setError("Couldn't load bookings.");
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (localStorage.getItem(PASS_KEY)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => { localStorage.removeItem(PASS_KEY); setAuthed(false); setRows([]); setPass(""); };

  const isoDate = (iso) => { try { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; } catch { return ""; } };

  const filtered = useMemo(() => rows.filter((r) => {
    if (dateFilter && isoDate(r.created_at) !== dateFilter) return false;
    if (payFilter === "paid" && !r.paid) return false;
    if (payFilter === "unpaid" && r.paid) return false;
    return true;
  }), [rows, dateFilter, payFilter]);

  const totals = useMemo(() => filtered.reduce((a, r) => {
    if (r.paid) { a.paid += Number(r.total || 0); a.tips += Number(r.tip || 0); a.paidCount += 1; }
    return a;
  }, { paid: 0, tips: 0, paidCount: 0 }), [filtered]);

  const emailRecap = async () => {
    setRecapBusy(true);
    try {
      const passcode = localStorage.getItem(PASS_KEY) ?? "";
      const { data } = await axios.post(`${API}/admin/daily-summary`,
        dateFilter ? { date: dateFilter } : {},
        { headers: { "X-Admin-Passcode": passcode } });
      toast.success(`Recap for ${data.date} emailed — ${data.trips} trips, $${data.paid_total.toFixed(2)} online.`);
    } catch { toast.error("Couldn't send the recap email."); }
    finally { setRecapBusy(false); }
  };

  if (!authed) {
    return (
      <main className="min-h-screen bg-ink flex items-center justify-center px-4" data-testid="admin-login">
        <div className="w-full max-w-sm bg-white border-2 border-sun p-8 shadow-hard">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brandred mb-2 flex items-center gap-2">
            <Lock weight="fill" size={16} /> Owner access
          </p>
          <h1 className="font-display font-extrabold text-3xl tracking-tight text-ink mb-5">Booking History</h1>
          <form onSubmit={(e) => { e.preventDefault(); load(pass); }} className="space-y-3">
            <Input data-testid="admin-passcode-input" type="password" value={pass} autoFocus
              onChange={(e) => setPass(e.target.value)} placeholder="Enter passcode"
              className="border-2 border-ink rounded-none h-12 bg-white" />
            <button data-testid="admin-login-btn" type="submit" disabled={!pass || loading}
              className="w-full bg-sun text-ink font-bold py-3.5 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
              {loading ? "Checking…" : "Open dashboard"}
            </button>
            {error && <p data-testid="admin-login-error" className="text-brandred text-sm font-semibold">{error}</p>}
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-paper pt-28 pb-20 px-4 sm:px-8" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brandred mb-2 flex items-center gap-2">
              <Receipt weight="fill" size={16} /> Owner dashboard
            </p>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-ink">Booking History</h1>
            <p className="text-ink/60 mt-2 text-sm">{rows.length} entries · rides, quotes, events & payments</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="admin-recap-btn" onClick={emailRecap} disabled={recapBusy}
              className="flex items-center gap-2 bg-sun text-ink font-bold px-5 py-3 border-2 border-ink hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-60">
              <EnvelopeSimple weight="bold" size={18} /> {recapBusy ? "Sending…" : (dateFilter ? "Email this day's recap" : "Email today's recap")}
            </button>
            <button data-testid="admin-refresh" onClick={() => load()}
              className="flex items-center gap-2 bg-ink text-white font-bold px-5 py-3 border-2 border-ink hover:bg-sun hover:text-ink transition-colors">
              <ArrowClockwise weight="bold" size={18} /> Refresh
            </button>
            <button data-testid="admin-signout" onClick={signOut}
              className="flex items-center gap-2 bg-white text-ink font-bold px-4 py-3 border-2 border-ink hover:bg-brandred hover:text-white transition-colors">
              <SignOut weight="bold" size={18} /> Sign out
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-4 mb-6 bg-white border-2 border-ink p-4" data-testid="admin-filters">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-ink/50 mb-1">Date</label>
            <Input data-testid="filter-date" type="date" value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="border-2 border-ink rounded-none h-11 bg-white w-[180px]" />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-ink/50 mb-1">Payment</label>
            <div className="flex border-2 border-ink">
              {[["all", "All"], ["paid", "Paid"], ["unpaid", "Unpaid"]].map(([v, l]) => (
                <button key={v} data-testid={`filter-pay-${v}`} onClick={() => setPayFilter(v)}
                  className={`px-4 h-11 text-sm font-bold transition-colors ${payFilter === v ? "bg-ink text-white" : "bg-white text-ink hover:bg-sun"} ${v !== "all" ? "border-l-2 border-ink" : ""}`}>{l}</button>
              ))}
            </div>
          </div>
          {(dateFilter || payFilter !== "all") && (
            <button data-testid="filter-clear" onClick={() => { setDateFilter(""); setPayFilter("all"); }}
              className="h-11 px-4 text-sm font-bold text-brandred hover:underline">Clear filters</button>
          )}
          <div className="ml-auto text-sm text-ink/70" data-testid="filter-summary">
            <span className="font-bold text-ink">{filtered.length}</span> shown ·
            <span className="font-bold text-ink"> {totals.paidCount}</span> paid ·
            <span className="font-bold text-ink"> ${totals.paid.toFixed(2)}</span> collected
            {totals.tips > 0 && <> · <span className="font-bold text-ink">${totals.tips.toFixed(2)}</span> tips</>}
          </div>
        </div>

        {loading ? (
          <p className="text-ink/50" data-testid="admin-loading">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-ink/50" data-testid="admin-empty">{rows.length === 0 ? "No bookings yet." : "No entries match these filters."}</p>
        ) : (
          <div className="overflow-x-auto border-2 border-ink bg-white" data-testid="admin-table">
            <table className="w-full text-sm min-w-[1050px]">
              <thead>
                <tr className="bg-ink text-white text-left">
                  {["When", "Type", "Service", "Customer", "Phone", "Route", "When (trip)", "Fare", "Tip", "Total", "Status"].map((h) => (
                    <th key={h} className="px-3 py-3 font-bold uppercase text-[11px] tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id || i} data-testid={`admin-row-${i}`} className="border-t border-ink/10 hover:bg-paper align-top">
                    <td className="px-3 py-3 whitespace-nowrap text-ink/70">{fmtWhen(r.created_at)}</td>
                    <td className="px-3 py-3 whitespace-nowrap"><span className="font-bold">{r.type}</span></td>
                    <td className="px-3 py-3 max-w-[180px]">{r.service_type || "—"}</td>
                    <td className="px-3 py-3 whitespace-nowrap font-semibold">{r.name || "—"}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {r.phone ? <a href={`tel:${r.phone}`} className="text-brandblue font-semibold flex items-center gap-1"><Phone size={13} weight="fill" />{r.phone}</a> : "—"}
                    </td>
                    <td className="px-3 py-3 max-w-[220px] text-ink/70">
                      {r.pickup || r.dropoff ? <span>{r.pickup || "?"} <span className="text-ink/40">→</span> {r.dropoff || "?"}</span> : "—"}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-ink/70">{[r.date, r.time].filter(Boolean).join(" ") || "—"}</td>
                    <td className="px-3 py-3 whitespace-nowrap">{money(r.price)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">{r.tip ? money(r.tip) : "—"}</td>
                    <td className="px-3 py-3 whitespace-nowrap font-bold">{money(r.total)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`text-[11px] font-bold px-2.5 py-1 ${statusStyle(r.status)}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
